import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Caption1,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Input,
  Menu,
  MenuButton,
  MenuItemCheckbox,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Switch,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  AppsRegular,
  ArrowSyncRegular,
  CheckmarkCircleRegular,
  CloudRegular,
  DeleteRegular,
  PeopleRegular,
  PeopleTeamRegular,
  SaveRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import type {
  DirectoryUserDto,
  EnterpriseAppDto,
  GroupSummaryDto,
  IntuneAppOptionDto,
  RoleDetailDto,
  RoleDriftDto,
  RoleGroupKind,
  RoleSummaryDto,
} from '../api/types';

const COLOR = {
  role: '#5B5FC7',
  user: '#D85A30',
  groupPrincipal: '#8764B8',
  Access: '#BA7517',
  Intune: '#1D9E75',
  Group: '#378ADD',
  AppRole: '#C239B3',
} as const;

type Pill = 'User' | 'Group' | 'Intune' | 'EApp';

interface NodeSpec {
  id: string;
  label: string;
  glyph: string;
  color: string;
  cls: string;
  side: 'L' | 'R' | 'C';
  x: number;
  y: number;
  selected: boolean;
  onSelect: () => void;
  onRemove?: () => void;
}

/** Ein in der rechten Liste markierter Eintrag, der per Klick in die Rolle übernommen wird. */
interface SelItem {
  id: string;
  label: string;
  kind: Pill;
  groupId?: string;
  groupName?: string;
  appLabel?: string;
  // Enterprise-App-Rolle:
  servicePrincipalId?: string;
  appName?: string;
  appRoleId?: string;
  appRoleName?: string;
}

const initials = (s: string) =>
  (s || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || '?';

function spread(count: number, top: number, bottom: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [(top + bottom) / 2];
  const step = (bottom - top) / (count - 1);
  return Array.from({ length: count }, (_, i) => top + i * step);
}

interface Props {
  roleId: string | null;
  onRoleSelected: (id: string | null) => void;
  onChanged: () => void;
  roles: RoleSummaryDto[];
}

export function IamEditorTab({ roleId, onRoleSelected, onChanged, roles }: Props) {
  const api = useApi();
  const [detail, setDetail] = useState<RoleDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Kopf-Formular (Name/Kategorie/Beschreibung).
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);

  // Pill-Auswahl + rechte Liste.
  const [pill, setPill] = useState<Pill>('User');
  const [query, setQuery] = useState('');
  const [userResults, setUserResults] = useState<DirectoryUserDto[]>([]);
  const [groupResults, setGroupResults] = useState<GroupSummaryDto[]>([]);
  const [eappResults, setEappResults] = useState<EnterpriseAppDto[]>([]);
  const [intuneApps, setIntuneApps] = useState<IntuneAppOptionDto[]>([]);
  const [intuneLoading, setIntuneLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [userCols, setUserCols] = useState<string[]>([]); // 'department' | 'manager' | 'jobTitle'
  const [selected, setSelected] = useState<Record<string, SelItem>>({});
  const [assigning, setAssigning] = useState(false);
  const searchTimer = useRef<number | undefined>(undefined);

  // Drift / Anwenden.
  const [drift, setDrift] = useState<RoleDriftDto | null>(null);
  const [driftLoading, setDriftLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Canvas-Breite messen für das Auto-Layout.
  const canvasRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(700);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [detail !== null]);

  const loadRole = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      setDrift(null);
      try {
        const r = await api.getRole(id);
        setDetail(r);
        setName(r.name);
        setCategory(r.category ?? '');
        setDescription(r.description ?? '');
        setEnabled(r.enabled);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    if (roleId) {
      void loadRole(roleId);
    } else {
      setDetail(null);
      setName('');
      setCategory('');
      setDescription('');
      setEnabled(true);
      setDrift(null);
    }
    setSelectedNode(null);
    setMessage(null);
  }, [roleId, loadRole]);

  // Intune-Apps einmalig laden (für die Intune-Pill).
  useEffect(() => {
    if (intuneApps.length > 0 || intuneLoading) return;
    setIntuneLoading(true);
    api
      .getRoleIntuneApps()
      .then(setIntuneApps)
      .catch(() => setIntuneApps([]))
      .finally(() => setIntuneLoading(false));
  }, [api, intuneApps.length, intuneLoading]);

  // Suche (User/Gruppen) bei Eingabe; Intune wird clientseitig gefiltert.
  function onQuery(value: string) {
    setQuery(value);
    setSearchError(null);
    if (pill === 'Intune') return;
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    const term = value.trim();
    if (term.length < 2) {
      setUserResults([]);
      setGroupResults([]);
      return;
    }
    setSearching(true);
    searchTimer.current = window.setTimeout(() => {
      const fail = (e: unknown) => setSearchError(e instanceof Error ? e.message : String(e));
      if (pill === 'User') {
        api.searchUsers(term).then(setUserResults).catch((e) => { setUserResults([]); fail(e); }).finally(() => setSearching(false));
      } else if (pill === 'Group') {
        api.listGroups(term).then((g) => setGroupResults(g.slice(0, 25))).catch((e) => { setGroupResults([]); fail(e); }).finally(() => setSearching(false));
      } else {
        api.searchEnterpriseApps(term).then(setEappResults).catch((e) => { setEappResults([]); fail(e); }).finally(() => setSearching(false));
      }
    }, 300);
  }

  function switchPill(p: Pill) {
    setPill(p);
    setQuery('');
    setUserResults([]);
    setGroupResults([]);
    setEappResults([]);
    setSearchError(null);
    setSelected({});
  }

  function toggleHub(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleSel(item: SelItem) {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = item;
      return next;
    });
  }

  async function createRole() {
    if (!name.trim()) return;
    setSavingMeta(true);
    setError(null);
    try {
      const r = await api.createRole({ name: name.trim(), category: category.trim() || undefined, description: description.trim() || undefined });
      setDetail(r);
      onRoleSelected(r.id);
      onChanged();
      setMessage('Rolle angelegt.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingMeta(false);
    }
  }

  async function saveMeta() {
    if (!detail || !name.trim()) return;
    setSavingMeta(true);
    setError(null);
    try {
      const r = await api.updateRole(detail.id, { name: name.trim(), category: category.trim() || undefined, description: description.trim() || undefined, enabled });
      setDetail(r);
      onChanged();
      setMessage('Gespeichert.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingMeta(false);
    }
  }

  async function removeRole() {
    if (!detail) return;
    if (!window.confirm(`Rolle „${detail.name}" wirklich löschen? Bestehende Gruppenmitgliedschaften in Entra bleiben unverändert.`)) return;
    try {
      await api.deleteRole(detail.id);
      onRoleSelected(null);
      onChanged();
      setMessage('Rolle gelöscht.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /** Übernimmt die markierten Einträge der aktiven Pill in die Rolle. */
  async function assignSelected() {
    if (!detail) return;
    const items = Object.values(selected);
    if (items.length === 0) return;
    setAssigning(true);
    setError(null);
    try {
      for (const it of items) {
        if (it.kind === 'Intune') {
          await api.addRoleGroup(detail.id, { groupId: it.groupId!, groupName: it.groupName, kind: 'Intune', appLabel: it.appLabel });
        } else if (it.kind === 'EApp') {
          await api.addRoleAppRole(detail.id, {
            servicePrincipalId: it.servicePrincipalId!,
            appName: it.appName ?? it.label,
            appRoleId: it.appRoleId ?? '00000000-0000-0000-0000-000000000000',
            appRoleName: it.appRoleName,
          });
        } else {
          await api.addRoleAssignment(detail.id, { principalId: it.id, principalType: it.kind, principalDisplay: it.label });
        }
      }
      setSelected({});
      setMessage(`${items.length} Eintrag/Einträge übernommen.`);
      await loadRole(detail.id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAssigning(false);
    }
  }

  async function removeAssignment(rowId: string) {
    if (!detail) return;
    setError(null);
    try {
      await api.removeRoleAssignment(detail.id, rowId);
      setSelectedNode(null);
      await loadRole(detail.id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function removeGroup(rowId: string) {
    if (!detail) return;
    setError(null);
    try {
      await api.removeRoleGroup(detail.id, rowId);
      setSelectedNode(null);
      await loadRole(detail.id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function removeAppRole(rowId: string) {
    if (!detail) return;
    setError(null);
    try {
      await api.removeRoleAppRole(detail.id, rowId);
      setSelectedNode(null);
      await loadRole(detail.id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function checkDrift() {
    if (!detail) return;
    setDriftLoading(true);
    setError(null);
    try {
      setDrift(await api.getRoleDrift(detail.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDriftLoading(false);
    }
  }

  // Erst Drift laden und Vorschau zeigen, dann nach Bestätigung schreiben.
  async function openApplyPreview() {
    if (!detail) return;
    setApplying(true);
    setError(null);
    setMessage(null);
    try {
      const d = await api.getRoleDrift(detail.id);
      setDrift(d);
      if (d.count === 0) setMessage('Soll = Ist – es ist nichts anzuwenden.');
      else setPreviewOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApplying(false);
    }
  }

  async function confirmApply() {
    if (!detail) return;
    setApplying(true);
    setError(null);
    setMessage(null);
    try {
      const before = drift?.count ?? (await api.getRoleDrift(detail.id)).count;
      const result = await api.applyRole(detail.id);
      const applied = before - result.count;
      setDrift(result);
      setPreviewOpen(false);
      setMessage(
        result.count === 0
          ? `Soll-Zustand angewandt: ${applied} Mitgliedschaft(en) ergänzt. Keine Abweichung mehr.`
          : `${applied} ergänzt, ${result.count} fehlgeschlagen (siehe Liste).`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApplying(false);
    }
  }

  const H = 600;
  const { nodes, edges } = useMemo(() => {
    const nodes: NodeSpec[] = [];
    const edges: { x1: number; y1: number; x2: number; y2: number }[] = [];
    if (!detail) return { nodes, edges };
    const W = width;
    const cx = W / 2;
    const cy = H / 2;

    nodes.push({
      id: `role:${detail.id}`,
      label: detail.name,
      glyph: initials(detail.name),
      color: COLOR.role,
      cls: 'role',
      side: 'C',
      x: cx,
      y: cy,
      selected: false,
      onSelect: () => setSelectedNode(null),
    });

    interface Leaf { id: string; label: string; color: string; onRemove: () => void; }
    interface Hub { key: string; label: string; color: string; leaves: Leaf[]; }

    const users = detail.assignments.filter((a) => a.principalType === 'User');
    const grpPrincipals = detail.assignments.filter((a) => a.principalType === 'Group');
    const leftHubs: Hub[] = [];
    if (users.length)
      leftHubs.push({ key: 'L:User', label: 'User', color: COLOR.user, leaves: users.map((a) => ({ id: `assign:${a.id}`, label: a.principalDisplay, color: COLOR.user, onRemove: () => void removeAssignment(a.id) })) });
    if (grpPrincipals.length)
      leftHubs.push({ key: 'L:Group', label: 'Gruppen', color: COLOR.groupPrincipal, leaves: grpPrincipals.map((a) => ({ id: `assign:${a.id}`, label: a.principalDisplay, color: COLOR.groupPrincipal, onRemove: () => void removeAssignment(a.id) })) });

    const byKind = (k: RoleGroupKind) => detail.groups.filter((g) => g.kind === k);
    const rightHubs: Hub[] = [];
    const acc = byKind('Access');
    if (acc.length) rightHubs.push({ key: 'R:Access', label: 'App-Zugriff', color: COLOR.Access, leaves: acc.map((g) => ({ id: `group:${g.id}`, label: g.appLabel ?? g.groupName, color: COLOR.Access, onRemove: () => void removeGroup(g.id) })) });
    const intu = byKind('Intune');
    if (intu.length) rightHubs.push({ key: 'R:Intune', label: 'Intune', color: COLOR.Intune, leaves: intu.map((g) => ({ id: `group:${g.id}`, label: g.appLabel ?? g.groupName, color: COLOR.Intune, onRemove: () => void removeGroup(g.id) })) });
    const dir = byKind('Group');
    if (dir.length) rightHubs.push({ key: 'R:Group', label: 'Direkte Gruppe', color: COLOR.Group, leaves: dir.map((g) => ({ id: `group:${g.id}`, label: g.appLabel ?? g.groupName, color: COLOR.Group, onRemove: () => void removeGroup(g.id) })) });
    if (detail.appRoles.length)
      rightHubs.push({
        key: 'R:AppRole',
        label: 'App-Rolle',
        color: COLOR.AppRole,
        leaves: detail.appRoles.map((ar) => ({
          id: `approle:${ar.id}`,
          label: ar.appRoleName ? `${ar.appName} · ${ar.appRoleName}` : ar.appName,
          color: COLOR.AppRole,
          onRemove: () => void removeAppRole(ar.id),
        })),
      });

    const place = (hubs: Hub[], side: 'L' | 'R') => {
      const hubX = side === 'L' ? W * 0.35 : W * 0.65;
      const leafX = side === 'L' ? W * 0.18 : W * 0.82;
      const cls = side === 'L' ? 'principal' : 'resource';
      const N = Math.max(1, hubs.length);
      const bandH = H / N;
      hubs.forEach((hub, i) => {
        const bandTop = i * bandH;
        const hy = bandTop + bandH / 2;
        const open = !collapsed.has(hub.key);
        nodes.push({
          id: hub.key,
          label: hub.label,
          glyph: String(hub.leaves.length),
          color: hub.color,
          cls: `hub${open ? ' open' : ''}`,
          side,
          x: hubX,
          y: hy,
          selected: false,
          onSelect: () => toggleHub(hub.key),
        });
        edges.push({ x1: cx, y1: cy, x2: hubX, y2: hy });
        if (open) {
          const ys = spread(hub.leaves.length, bandTop + 46, bandTop + bandH - 46);
          hub.leaves.forEach((lf, j) => {
            nodes.push({
              id: lf.id,
              label: lf.label,
              glyph: initials(lf.label),
              color: lf.color,
              cls,
              side,
              x: leafX,
              y: ys[j],
              selected: selectedNode === lf.id,
              onSelect: () => setSelectedNode((p) => (p === lf.id ? null : lf.id)),
              onRemove: lf.onRemove,
            });
            edges.push({ x1: hubX, y1: hy, x2: leafX, y2: ys[j] });
          });
        }
      });
    };

    place(leftHubs, 'L');
    place(rightHubs, 'R');
    return { nodes, edges };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, width, selectedNode, collapsed]);

  // ---- Render: neue Rolle ----
  if (!roleId && !detail) {
    return (
      <div className="iam-card" style={{ maxWidth: 520 }}>
        <Text weight="semibold">Neue Rolle anlegen</Text>
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          <Input placeholder="Name (z. B. Vertrieb Außendienst)" value={name} onChange={(_, d) => setName(d.value)} />
          <Input placeholder="Kategorie (optional, z. B. Abteilung)" value={category} onChange={(_, d) => setCategory(d.value)} />
          <Textarea placeholder="Beschreibung (optional)" value={description} onChange={(_, d) => setDescription(d.value)} resize="vertical" />
          <div>
            <Button appearance="primary" icon={<AddRegular />} disabled={!name.trim() || savingMeta} onClick={createRole}>
              Anlegen
            </Button>
          </div>
        </div>
        {error ? (
          <MessageBar intent="error" style={{ marginTop: 12 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}
      </div>
    );
  }

  if (loading && !detail) {
    return <Spinner label="Lade Rolle…" />;
  }

  return (
    <div>
      {/* Rollen-Wechsel */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Rolle</Caption1>
        <Dropdown
          style={{ minWidth: 240 }}
          selectedOptions={detail ? [detail.id] : []}
          value={detail?.name ?? ''}
          onOptionSelect={(_, d) => d.optionValue && onRoleSelected(d.optionValue)}
        >
          {roles.map((r) => (
            <Option key={r.id} value={r.id} text={r.name}>
              {r.name}
            </Option>
          ))}
        </Dropdown>
      </div>

      {message ? (
        <MessageBar intent="success" style={{ marginBottom: 12 }}>
          <MessageBarBody>{message}</MessageBarBody>
        </MessageBar>
      ) : null}
      {error ? (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {/* Kopf: Stammdaten */}
      <div className="iam-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px auto', gap: 10, alignItems: 'end' }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <Caption1>Name</Caption1>
            <Input value={name} onChange={(_, d) => setName(d.value)} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <Caption1>Kategorie</Caption1>
            <Input value={category} onChange={(_, d) => setCategory(d.value)} />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<SaveRegular />} disabled={savingMeta || !name.trim()} onClick={saveMeta}>
              Speichern
            </Button>
            <Button appearance="subtle" icon={<DeleteRegular />} onClick={removeRole}>
              Löschen
            </Button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center', marginTop: 10 }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <Caption1>Beschreibung</Caption1>
            <Textarea value={description} onChange={(_, d) => setDescription(d.value)} resize="vertical" />
          </label>
          <Switch
            checked={enabled}
            onChange={(_, d) => setEnabled(d.checked)}
            label={enabled ? 'Aktiv' : 'Inaktiv (wird nicht angewandt)'}
            labelPosition="after"
          />
        </div>
      </div>

      {/* Pill-Leiste */}
      <div className="iam-pillbar">
        <PillButton active={pill === 'User'} onClick={() => switchPill('User')} icon={<PeopleRegular />} label="User" />
        <PillButton active={pill === 'Group'} onClick={() => switchPill('Group')} icon={<PeopleTeamRegular />} label="Gruppen" />
        <PillButton active={pill === 'Intune'} onClick={() => switchPill('Intune')} icon={<AppsRegular />} label="Intune-App" />
        <PillButton active={pill === 'EApp'} onClick={() => switchPill('EApp')} icon={<CloudRegular />} label="Enterprise App" />
      </div>

      {/* Canvas + rechte Auswahl-Liste */}
      <div className="iam-editor-grid">
        <div className="iam-canvas" ref={canvasRef}>
          <div className="iam-col-label" style={{ left: 16 }}>Zugewiesen</div>
          <div className="iam-col-label" style={{ right: 16 }}>Berechtigungen</div>

          <svg className="iam-edges">
            {edges.map((e, i) => (
              <line key={i} className="iam-edge" x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} />
            ))}
          </svg>

          {nodes.map((n) => (
            <div
              key={n.id}
              className={`iam-node ${n.cls}${n.selected ? ' selected' : ''} side-${n.side === 'L' ? 'left' : n.side === 'R' ? 'right' : 'center'}`}
              style={{ left: n.x, top: n.y }}
              title={n.label}
              onClick={n.onSelect}
            >
              <div className="iam-circ" style={{ color: n.color, border: `2px solid ${n.color}` }}>
                {n.glyph}
                {n.onRemove ? (
                  <span
                    className="iam-node-x"
                    title="Entfernen"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      n.onRemove?.();
                    }}
                  >
                    ×
                  </span>
                ) : null}
              </div>
              <div className="iam-lbl pii">{n.label}</div>
            </div>
          ))}

          {detail && detail.assignments.length === 0 && detail.groups.length === 0 ? (
            <div className="iam-empty-canvas">
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Noch leer — rechts Einträge wählen und übernehmen.
              </Caption1>
            </div>
          ) : null}
        </div>

        <PickPanel
          pill={pill}
          query={query}
          onQuery={onQuery}
          searching={searching}
          searchError={searchError}
          userResults={userResults}
          groupResults={groupResults}
          eappResults={eappResults}
          intuneApps={intuneApps}
          intuneLoading={intuneLoading}
          userCols={userCols}
          setUserCols={setUserCols}
          selected={selected}
          toggleSel={toggleSel}
          assigning={assigning}
          onAssign={assignSelected}
        />
      </div>

      {/* Legende + Anwenden */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <div className="iam-legend">
          <span><span className="iam-dot" style={{ border: `2px solid ${COLOR.user}`, background: 'transparent' }} />Person</span>
          <span><span className="iam-dot" style={{ border: `2px solid ${COLOR.groupPrincipal}`, background: 'transparent' }} />Gruppe (Prinzipal)</span>
          <span><span className="iam-dot" style={{ border: `2px solid ${COLOR.Access}`, background: 'transparent' }} />App-Zugriff</span>
          <span><span className="iam-dot" style={{ border: `2px solid ${COLOR.Intune}`, background: 'transparent' }} />Intune</span>
          <span><span className="iam-dot" style={{ border: `2px solid ${COLOR.Group}`, background: 'transparent' }} />Direkte Gruppe</span>
          <span><span className="iam-dot" style={{ border: `2px solid ${COLOR.AppRole}`, background: 'transparent' }} />App-Rolle</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {drift ? (
            <Caption1 style={{ color: drift.count > 0 ? tokens.colorPaletteDarkOrangeForeground1 : tokens.colorPaletteGreenForeground1 }}>
              {drift.count > 0 ? `${drift.count} offene Mitgliedschaft(en)` : 'Soll = Ist'}
            </Caption1>
          ) : null}
          <Button icon={<ArrowSyncRegular />} disabled={driftLoading || !detail} onClick={checkDrift}>
            Drift prüfen
          </Button>
          <Button appearance="primary" icon={<CheckmarkCircleRegular />} disabled={applying || !detail} onClick={openApplyPreview}>
            {applying ? 'Prüfe…' : 'Soll anwenden'}
          </Button>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={(_, d) => setPreviewOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Soll-Zustand anwenden</DialogTitle>
            <DialogContent>
              <Text>
                Folgende {drift?.count ?? 0} Mitgliedschaft(en) werden in Entra ergänzt. Es wird
                ausschließlich hinzugefügt – nichts wird entfernt.
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12, maxHeight: 300, overflow: 'auto' }}>
                {(drift?.items ?? []).map((d, i) => (
                  <span key={i} className="iam-chip pii" title={`${d.principalDisplay} → ${d.targetName}`}>
                    {d.principalDisplay} → {d.targetName}
                  </span>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPreviewOpen(false)}>Abbrechen</Button>
              <Button appearance="primary" icon={<CheckmarkCircleRegular />} disabled={applying} onClick={confirmApply}>
                {applying ? 'Wende an…' : `Jetzt anwenden (${drift?.count ?? 0})`}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {drift && drift.count > 0 ? (
        <div className="iam-card" style={{ marginTop: 12 }}>
          <Text weight="semibold">Offene Mitgliedschaften ({drift.count})</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {drift.items.slice(0, 80).map((d, i) => (
              <span key={i} className="iam-chip pii" title={`${d.principalDisplay} → ${d.targetName}`}>
                {d.principalDisplay} → {d.targetName}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PillButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <button type="button" className={`iam-pillbtn${active ? ' active' : ''}`} onClick={onClick}>
      {icon}
      {label}
    </button>
  );
}

interface PickPanelProps {
  pill: Pill;
  query: string;
  onQuery: (v: string) => void;
  searching: boolean;
  searchError: string | null;
  userResults: DirectoryUserDto[];
  groupResults: GroupSummaryDto[];
  eappResults: EnterpriseAppDto[];
  intuneApps: IntuneAppOptionDto[];
  intuneLoading: boolean;
  userCols: string[];
  setUserCols: (v: string[]) => void;
  selected: Record<string, SelItem>;
  toggleSel: (item: SelItem) => void;
  assigning: boolean;
  onAssign: () => void;
}

function PickPanel(p: PickPanelProps) {
  const count = Object.keys(p.selected).length;
  const placeholder =
    p.pill === 'User' ? 'Benutzer suchen…'
      : p.pill === 'Group' ? 'Gruppe suchen…'
      : p.pill === 'EApp' ? 'Enterprise App suchen…'
      : 'Intune-App filtern…';
  const actionLabel = p.pill === 'Intune' || p.pill === 'EApp' ? 'Berechtigung hinzufügen' : 'Rolle zuweisen';

  const intuneVisible = useMemo(() => {
    const q = p.query.trim().toLowerCase();
    const list = q ? p.intuneApps.filter((a) => a.appName.toLowerCase().includes(q) || a.groupName.toLowerCase().includes(q)) : p.intuneApps;
    return list.slice(0, 100);
  }, [p.intuneApps, p.query]);

  // Enterprise Apps in wählbare (App × App-Rolle)-Zeilen aufflachen.
  const eappRows = useMemo(() => {
    const rows: { rowId: string; appName: string; roleLabel: string; sel: SelItem }[] = [];
    const ZERO = '00000000-0000-0000-0000-000000000000';
    for (const app of p.eappResults) {
      const roles = app.appRoles.length > 0 ? app.appRoles : [{ id: ZERO, displayName: 'Standardzugriff', value: undefined }];
      for (const r of roles) {
        const isDefault = r.id === ZERO;
        rows.push({
          rowId: `${app.id}:${r.id}`,
          appName: app.displayName,
          roleLabel: r.displayName,
          sel: {
            id: `${app.id}:${r.id}`,
            label: isDefault ? app.displayName : `${app.displayName} · ${r.displayName}`,
            kind: 'EApp',
            servicePrincipalId: app.id,
            appName: app.displayName,
            appRoleId: r.id,
            appRoleName: isDefault ? undefined : r.displayName,
          },
        });
      }
    }
    return rows;
  }, [p.eappResults]);

  return (
    <div className="iam-pick">
      <div className="iam-pick-head">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Input
            style={{ flex: 1 }}
            contentBefore={<SearchRegular />}
            placeholder={placeholder}
            value={p.query}
            onChange={(_, d) => p.onQuery(d.value)}
          />
          {p.pill === 'User' ? (
            <Menu
              checkedValues={{ cols: p.userCols }}
              onCheckedValueChange={(_, data) => p.setUserCols(data.checkedItems)}
            >
              <MenuTrigger disableButtonEnhancement>
                <MenuButton size="small">Spalten</MenuButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItemCheckbox name="cols" value="department">Abteilung</MenuItemCheckbox>
                  <MenuItemCheckbox name="cols" value="manager">Manager</MenuItemCheckbox>
                  <MenuItemCheckbox name="cols" value="jobTitle">Jobtitel</MenuItemCheckbox>
                </MenuList>
              </MenuPopover>
            </Menu>
          ) : null}
        </div>
      </div>

      <div className="iam-pick-list">
        {p.searchError ? (
          <div className="iam-pick-empty" style={{ color: tokens.colorPaletteRedForeground1 }}>{p.searchError}</div>
        ) : p.searching || (p.pill === 'Intune' && p.intuneLoading) ? (
          <div className="iam-pick-empty"><Spinner size="tiny" label="Lade…" /></div>
        ) : p.pill === 'User' ? (
          p.userResults.length === 0 ? (
            <div className="iam-pick-empty">{p.query.trim().length < 2 ? 'Tippe ≥ 2 Zeichen, um Benutzer zu suchen.' : 'Keine Treffer.'}</div>
          ) : (
            p.userResults.map((u) => {
              const sub: string[] = [];
              if (u.mail) sub.push(u.mail);
              if (p.userCols.includes('department') && u.department) sub.push(u.department);
              if (p.userCols.includes('jobTitle') && u.jobTitle) sub.push(u.jobTitle);
              if (p.userCols.includes('manager') && u.managerName) sub.push(`MGR: ${u.managerName}`);
              return (
                <Row
                  key={u.id}
                  checked={!!p.selected[u.id]}
                  onToggle={() => p.toggleSel({ id: u.id, label: u.displayName, kind: 'User' })}
                  name={u.displayName}
                  sub={sub}
                />
              );
            })
          )
        ) : p.pill === 'Group' ? (
          p.groupResults.length === 0 ? (
            <div className="iam-pick-empty">{p.query.trim().length < 2 ? 'Tippe ≥ 2 Zeichen, um Gruppen zu suchen.' : 'Keine Treffer.'}</div>
          ) : (
            p.groupResults.map((g) => (
              <Row
                key={g.id}
                checked={!!p.selected[g.id]}
                onToggle={() => p.toggleSel({ id: g.id, label: g.displayName, kind: 'Group' })}
                name={g.displayName}
                sub={[g.groupType]}
              />
            ))
          )
        ) : p.pill === 'EApp' ? (
          eappRows.length === 0 ? (
            <div className="iam-pick-empty">{p.query.trim().length < 2 ? 'Tippe ≥ 2 Zeichen, um Enterprise Apps zu suchen.' : 'Keine Treffer.'}</div>
          ) : (
            eappRows.map((row) => (
              <Row
                key={row.rowId}
                checked={!!p.selected[row.rowId]}
                onToggle={() => p.toggleSel(row.sel)}
                name={row.appName}
                sub={[row.roleLabel]}
              />
            ))
          )
        ) : intuneVisible.length === 0 ? (
          <div className="iam-pick-empty">Keine Intune-Apps gefunden.</div>
        ) : (
          intuneVisible.map((a) => (
            <Row
              key={`${a.appId}:${a.groupId}`}
              checked={!!p.selected[a.groupId]}
              onToggle={() => p.toggleSel({ id: a.groupId, label: a.appName, kind: 'Intune', groupId: a.groupId, groupName: a.groupName, appLabel: a.appName })}
              name={a.appName}
              sub={[a.groupName]}
            />
          ))
        )}
      </div>

      <div className="iam-pick-foot">
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{count} ausgewählt</Caption1>
        <Button
          style={{ marginLeft: 'auto' }}
          appearance="primary"
          icon={<AddRegular />}
          disabled={count === 0 || p.assigning}
          onClick={p.onAssign}
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

function Row({ checked, onToggle, name, sub }: { checked: boolean; onToggle: () => void; name: string; sub: string[] }) {
  return (
    <div className={`iam-pick-row${checked ? ' sel' : ''}`} onClick={onToggle}>
      <Checkbox checked={checked} onClick={(e) => e.stopPropagation()} onChange={onToggle} />
      <div className="iam-pick-main">
        <div className="iam-pick-name pii" title={name}>{name}</div>
        {sub.length > 0 ? (
          <div className="iam-pick-sub">
            {sub.map((s, i) => (
              <span key={i} className="pii">{s}</span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
